import { Injectable, Signal, signal } from '@angular/core';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';

type TranslationTable = Record<string, string>;

/**
 * The account `en` table, once its chunk has landed. Empty until `loadEnTranslations()` resolves.
 *
 * A SIGNAL rather than a plain module-level `let`, deliberately. Moving the dictionary behind a
 * dynamic import turns synchronous boot work into asynchronous work, and under zone.js a `.then()`
 * that assigns a plain field repaints for free — zone.js patches the microtask and ticks after it.
 * Under zoneless change detection that same assignment is invisible to Angular and the write
 * silently never reaches the screen. A signal write is a reactive notification, so a consumer that
 * reads it in a reactive context gets repainted by the framework instead. `markForCheck()` is not
 * the fix here: it only covers the OnPush-ancestor half, and would have to be redone.
 *
 * Be clear about what this does and does not buy TODAY: nothing renders before the write, because
 * `loadEnTranslations()` is awaited by an APP_INITIALIZER and Angular resolves every initializer
 * before `ngDoBootstrap` bootstraps the root component. The signal is what keeps this safe if that
 * ordering ever changes, and what gives a future reactive consumer a real dependency edge instead
 * of a stale field read.
 */
const enTranslations = signal<TranslationTable>({});

/** Read-only view of the table, for consumers that want to react to it landing. */
export const enTranslationsTable: Signal<TranslationTable> = enTranslations.asReadonly();

/**
 * Fetch the `en` dictionary chunk and publish it.
 *
 * `en.translations.ts` is ~116 KB raw (~28 KB gzip) and used to reach the entry graph through a
 * static import in this file, for a table nothing reads until a template renders. It is its own
 * chunk now.
 *
 * 🚨 Callers must AWAIT this before anything renders — see the i18n APP_INITIALIZER in
 * app.module.ts. It must not become a fire-and-forget `.then()`: `LegacyTranslatePipe` is a PURE
 * pipe over `TranslateService.instant`, so a template that evaluates before the table lands caches
 * the raw key as its output and never re-runs.
 */
export async function loadEnTranslations(): Promise<TranslationTable> {
    const { EN_TRANSLATIONS } = await import('./en.translations');
    enTranslations.set(EN_TRANSLATIONS);
    return EN_TRANSLATIONS;
}

/**
 * ngx-translate loader that serves the account UI dictionary from the `EN_TRANSLATIONS` table
 * (extracted verbatim from the AngularJS angular-translate `language.js` registrations). The
 * account UI is single-locale (`en`, `$translateProvider.preferredLanguage('en')` — no HTTP
 * loader, no runtime locale switching). Any requested lang maps to the same `en` table (parity
 * with angular-translate, which only ever registered `en`).
 *
 * 🚨 THIS MUST STAY SYNCHRONOUS. There are 46 `TranslateService.instant()` call sites in the app;
 * an HTTP or otherwise-async loader turns every one of them into a race that renders the raw key.
 * The chunk is fetched ahead of first paint by `loadEnTranslations()`, and this only ever hands
 * back what has already arrived.
 */
@Injectable()
export class InBundleTranslateLoader implements TranslateLoader {
    getTranslation(_lang: string): Observable<TranslationTable> {
        return of(enTranslations());
    }
}
